import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';
import { trackEmailClick, trackFormSubmission, trackPhoneCall } from '../../components/Analytics/Analytics';
import './AplicaIA2026Page.css';

const WEBHOOK_URL =
  process.env.REACT_APP_APLICA_IA_WEBHOOK_URL ||
  'https://hook.eu1.make.com/ag4wg4gyru5tebyiuyhnre6iz25ekidj';

const CONTACT_EMAIL = process.env.REACT_APP_APLICA_IA_EMAIL_TO || 'comercial@e-asy.es';
const CONTACT_PHONE = '+34 822 68 50 12';

const beneficios = [
  {
    icon: '🛒',
    titulo: 'E-commerce conectado al negocio',
    descripcion:
      'Tienda online preparada para vender de verdad, conectada con catálogo, pedidos, procesos internos y evolución futura.',
  },
  {
    icon: '👥',
    titulo: 'Portal de clientes',
    descripcion:
      'Un espacio privado para que tu clientela consulte información, haga seguimiento y mejore la relación con tu empresa.',
  },
  {
    icon: '📊',
    titulo: 'IA que analiza el comportamiento',
    descripcion:
      'Detecta interés, abandono, picos de demanda, productos estrella y patrones de compra para decidir con datos.',
  },
  {
    icon: '💬',
    titulo: 'Presupuestos asistidos por IA',
    descripcion:
      'La IA conversa con el cliente potencial, entiende su necesidad y genera una estimación aproximada no vinculante.',
  },
  {
    icon: '🧩',
    titulo: 'Proyecto a medida',
    descripcion:
      'No forzamos un producto cerrado: adaptamos la solución al proceso, al sector y al punto de partida de cada empresa.',
  },
  {
    icon: '€',
    titulo: 'Enfoque subvencionable',
    descripcion:
      'Diseñamos el proyecto para que encaje con la lógica del programa y tramitamos contigo la oportunidad de ayuda.',
  },
];

const pilares = [
  {
    titulo: 'IA de analítica comercial',
    descripcion:
      'Recoge señales de navegación y compra para detectar cuándo una persona gana o pierde interés, qué campañas funcionan mejor y qué productos tiran de la demanda.',
  },
  {
    titulo: 'Consulta en lenguaje natural',
    descripcion:
      'El equipo puede preguntar a la plataforma cosas como "qué productos convierten mejor", "cuándo cae el interés" o "qué segmentos abandonan más".',
  },
  {
    titulo: 'IA para prepresupuestación',
    descripcion:
      'El sistema guía al cliente potencial con preguntas sucesivas hasta entender el servicio que busca y ofrecer una estimación rápida.',
  },
  {
    titulo: 'Aprendizaje con histórico',
    descripcion:
      'La lógica de presupuesto puede alimentarse con servicios y presupuestos previos para afinar aproximaciones y ahorrar tiempo comercial.',
  },
];

const encajeAyuda = [
  'Empresas industriales y de servicios conexos ligados a la industria con centro de actividad en Euskadi.',
  'Subvención a fondo perdido del 60% de los gastos elegibles, hasta 100.000 euros por empresa.',
  'Programa orientado a proyectos reales de adopción de IA, estrategia del dato, implantación y capacitación.',
  'Plazo de presentación abierto hasta el 02/11/2026 a las 23:59, según la información publicada por SPRI.',
];

const entregables = [
  'Definición funcional del e-commerce y del portal de clientes.',
  'Diseño del flujo de datos para analítica, seguimiento y toma de decisiones.',
  'Implantación de la IA para analítica comercial y consulta en lenguaje natural.',
  'Implantación de la IA de presupuestos con lógica conversacional y estimación no vinculante.',
  'Automatizaciones, integraciones y formación del equipo.',
];

const faqs = [
  {
    q: '¿Este servicio es un producto cerrado?',
    a: 'No. Partimos de un núcleo potente, pero adaptamos el proyecto a tu catálogo, tus procesos, tu operativa comercial y tus objetivos.',
  },
  {
    q: '¿La ayuda cubre solo software?',
    a: 'No necesariamente. La convocatoria contempla distintas tipologías de gasto elegible, como consultoría e ingeniería, software, hardware y servicios en la nube, siempre según las bases y el encaje del proyecto.',
  },
  {
    q: '¿Podéis tramitar la subvención con nosotros?',
    a: 'Sí. Te ayudamos a enfocar el proyecto, preparar el planteamiento y presentarlo con una propuesta alineada con la convocatoria.',
  },
  {
    q: '¿La IA de presupuestos sustituye al equipo comercial?',
    a: 'No. Su función es filtrar, orientar y acelerar el trabajo previo para que el equipo invierta tiempo en oportunidades mejor cualificadas.',
  },
];

const initialFormData = {
  nombre: '',
  empresa: '',
  email: '',
  telefono: '',
  territorio: '',
  sector: '',
  interes: 'ecommerce-portal-ia',
  mensaje: '',
  consentimiento: false,
};

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const AplicaIA2026Page = ({ darkMode, toggleDarkMode }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhoneClick = () => {
    trackPhoneCall(CONTACT_PHONE);
  };

  const handleEmailClick = () => {
    trackEmailClick(CONTACT_EMAIL);
  };

  const buildEmailHtml = () => {
    const interesMap = {
      'ecommerce-portal-ia': 'E-commerce + portal de clientes + IA',
      'solo-analitica-ia': 'Solo analitica con IA',
      'solo-presupuestos-ia': 'Solo IA para presupuestos',
      'proyecto-medida': 'Proyecto a medida',
    };

    return [
      '<h3>Nuevo lead desde la landing Aplica Inteligencia Artificial 2026</h3>',
      '<p>Se ha recibido una nueva solicitud desde la web de E-ASY.</p>',
      `<p><strong>Nombre:</strong> ${escapeHtml(formData.nombre)}</p>`,
      `<p><strong>Empresa:</strong> ${escapeHtml(formData.empresa)}</p>`,
      `<p><strong>Email:</strong> ${escapeHtml(formData.email)}</p>`,
      `<p><strong>Telefono:</strong> ${escapeHtml(formData.telefono || 'No indicado')}</p>`,
      `<p><strong>Territorio / ubicacion:</strong> ${escapeHtml(formData.territorio || 'No indicado')}</p>`,
      `<p><strong>Sector:</strong> ${escapeHtml(formData.sector || 'No indicado')}</p>`,
      `<p><strong>Interes principal:</strong> ${escapeHtml(interesMap[formData.interes] || formData.interes)}</p>`,
      `<p><strong>Mensaje:</strong><br>${escapeHtml(formData.mensaje || 'Sin mensaje adicional').replace(/\n/g, '<br>')}</p>`,
      `<p><strong>URL de origen:</strong> ${escapeHtml(window.location.href)}</p>`,
    ].join('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: CONTACT_EMAIL,
          cc: '',
          cc1: '',
          cc2: '',
          cc3: '',
          subject: `[Aplica IA 2026] ${formData.empresa || formData.nombre}`,
          text: buildEmailHtml(),
        }),
      });

      if (!response.ok) {
        throw new Error('No se pudo enviar el formulario');
      }

      trackFormSubmission('aplica_ia_2026_form');
      setEnviado(true);
      setFormData(initialFormData);
    } catch (submitError) {
      setError(`No se pudo enviar la solicitud. Escríbenos a ${CONTACT_EMAIL}.`);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <SEO
        title="Aplica Inteligencia Artificial 2026 | E-commerce con IA y portal de clientes | E-ASY"
        description="Landing de E-ASY para empresas que buscan la ayuda Aplica Inteligencia Artificial 2026 de SPRI. E-commerce con portal de clientes, IA para analitica comercial, IA para presupuestos, proyectos a medida y tramitacion de la subvencion."
        keywords="Aplica Inteligencia Artificial 2026, SPRI inteligencia artificial, subvencion inteligencia artificial Euskadi, ecommerce con IA, portal de clientes, IA para presupuestos, IA para analitica comercial, proyectos a medida IA, E-ASY"
        canonical="https://e-asy.es/servicios/aplica-inteligencia-artificial-2026"
      />

      <AnalyticsTracker
        pageTitle="Aplica Inteligencia Artificial 2026 - E-ASY"
        pageLocation="/servicios/aplica-inteligencia-artificial-2026"
        darkMode={darkMode}
      />

      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className={`aplica-ia-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="aplica-ia-hero">
          <div className="aplica-ia-hero-content">
            <div className="aplica-ia-hero-text">
              <span className="aplica-ia-eyebrow">SPRI · Aplica Inteligencia Artificial 2026</span>
              <h1 className="aplica-ia-title">E-commerce con IA y portal de clientes para empresas que quieren aprovechar la ayuda de SPRI</h1>
              <p className="aplica-ia-subtitle">
                En E-ASY planteamos un servicio pensado para <strong>convertirse en un proyecto real</strong>: un
                <strong> e-commerce con portal de clientes</strong> que incorpora IA en dos puntos clave del negocio.
              </p>
              <p className="aplica-ia-subtitle">
                Por un lado, una IA que <strong>analiza el comportamiento de la clientela</strong>. Por otro, una IA
                que <strong>ayuda a preparar presupuestos aproximados no vinculantes</strong> para ahorrar tiempo
                comercial. Además, <strong>tramitamos la subvención</strong>, <strong>no repercutimos IVA</strong> y
                adaptamos cada implantación a la realidad de tu empresa.
              </p>

              <div className="aplica-ia-hero-actions">
                <a href="#formulario-aplica-ia" className="aplica-ia-button primary">
                  Solicitar estudio del proyecto
                </a>
                <Link to="/servicios" className="aplica-ia-button secondary">
                  Ver más servicios
                </Link>
              </div>

              <div className="aplica-ia-stats">
                <div className="aplica-ia-stat">
                  <strong>60%</strong>
                  <span>de los gastos elegibles</span>
                </div>
                <div className="aplica-ia-stat">
                  <strong>100.000€</strong>
                  <span>máximo por empresa</span>
                </div>
                <div className="aplica-ia-stat">
                  <strong>02/11/2026</strong>
                  <span>fecha límite publicada</span>
                </div>
              </div>
            </div>

            <div className="aplica-ia-hero-card">
              <div className="hero-card-panel">
                <h2>Qué hace diferente esta propuesta</h2>
                <ul>
                  <li>No es solo una tienda online: es una plataforma de relación con clientes.</li>
                  <li>La IA ayuda a entender mejor el comportamiento comercial.</li>
                  <li>La IA reduce el esfuerzo previo de presupuestación.</li>
                  <li>El proyecto puede plantearse a medida y alineado con la convocatoria.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="aplica-ia-section">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Un servicio pensado para aportar valor real</h2>
              <p>
                La convocatoria de SPRI busca implantaciones efectivas. Por eso proponemos una solución que mezcla
                negocio digital, dato, automatización y adopción práctica de la IA.
              </p>
            </div>

            <div className="aplica-ia-grid cards-3">
              {beneficios.map((beneficio) => (
                <article key={beneficio.titulo} className="aplica-ia-card">
                  <div className="aplica-ia-card-icon">{beneficio.icon}</div>
                  <h3>{beneficio.titulo}</h3>
                  <p>{beneficio.descripcion}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="aplica-ia-section aplica-ia-alt">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Las dos capacidades de IA que más impacto pueden tener</h2>
              <p>
                El producto se apoya en dos líneas muy claras: inteligencia para entender mejor a la clientela e
                inteligencia para reducir el coste comercial de preparar presupuestos.
              </p>
            </div>

            <div className="aplica-ia-grid cards-2">
              {pilares.map((pilar) => (
                <article key={pilar.titulo} className="aplica-ia-card feature">
                  <h3>{pilar.titulo}</h3>
                  <p>{pilar.descripcion}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="aplica-ia-section">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Cómo encaja con Aplica Inteligencia Artificial 2026</h2>
              <p>
                Hemos tomado como referencia la información pública de SPRI para plantear un enfoque coherente con la
                ayuda, sin sustituir la revisión final de las bases y de la elegibilidad concreta de tu proyecto.
              </p>
            </div>

            <div className="aplica-ia-highlight-grid">
              <div className="aplica-ia-highlight">
                <h3>Claves de la convocatoria</h3>
                <ul>
                  {encajeAyuda.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="aplica-ia-highlight emphasis">
                <h3>Qué podemos preparar contigo</h3>
                <ul>
                  {entregables.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="aplica-ia-section aplica-ia-alt">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Tramitamos la subvención y diseñamos proyectos a medida</h2>
              <p>
                Si tu empresa está valorando esta ayuda, podemos ayudarte tanto a definir el proyecto como a convertir
                la idea en una propuesta clara, defendible y útil para el negocio.
              </p>
            </div>

            <div className="aplica-ia-sell-boxes">
              <article className="sell-box">
                <h3>Tramitación con enfoque práctico</h3>
                <p>
                  No nos limitamos a hablar de tecnología. Trabajamos el encaje del proyecto con la convocatoria y te
                  acompañamos en la preparación de la solicitud.
                </p>
              </article>
              <article className="sell-box">
                <h3>Sin IVA repercutido</h3>
                <p>
                  En esta línea trabajamos con un planteamiento comercial orientado a que el proyecto sea más viable y
                  atractivo para la empresa solicitante.
                </p>
              </article>
              <article className="sell-box">
                <h3>Producto potente, no plantilla cerrada</h3>
                <p>
                  Partimos de una base muy sólida, pero cada implantación se adapta a catálogo, procesos, servicio,
                  canal comercial y nivel de madurez digital.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="aplica-ia-section">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Preguntas frecuentes</h2>
              <p>Dudas habituales antes de plantear un proyecto vinculado a esta ayuda.</p>
            </div>

            <div className="aplica-ia-faqs">
              {faqs.map((item) => (
                <article key={item.q} className="faq-item">
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="formulario-aplica-ia" className="aplica-ia-section aplica-ia-form-section">
          <div className="aplica-ia-section-content form-layout">
            <div className="aplica-ia-form-copy">
              <span className="aplica-ia-eyebrow">Solicitud de información</span>
              <h2>Cuéntanos tu caso y te ayudamos a plantearlo</h2>
              <p>
                Si estás valorando solicitar <strong>Aplica Inteligencia Artificial 2026</strong>, podemos revisar el
                encaje de tu idea, proponerte un enfoque de proyecto y explicarte cómo lo llevaríamos a producción.
              </p>
              <div className="contact-mini-cards">
                <div className="mini-card">
                  <span>Email</span>
                  <a href={`mailto:${CONTACT_EMAIL}`} onClick={handleEmailClick}>
                    {CONTACT_EMAIL}
                  </a>
                </div>
                <div className="mini-card">
                  <span>Teléfono</span>
                  <a href={`tel:${CONTACT_PHONE.replace(/\s+/g, '')}`} onClick={handlePhoneClick}>
                    {CONTACT_PHONE}
                  </a>
                </div>
              </div>
            </div>

            <div className="aplica-ia-form-card">
              {enviado ? (
                <div className="aplica-ia-success">
                  <h3>Solicitud enviada</h3>
                  <p>
                    Hemos recibido tus datos correctamente. Revisaremos el caso y te contactaremos para valorar el
                    encaje del proyecto con la ayuda.
                  </p>
                  <a href="/servicios/aplica-inteligencia-artificial-2026" className="aplica-ia-button primary">
                    Enviar otra solicitud
                  </a>
                </div>
              ) : (
                <form className="aplica-ia-form" onSubmit={handleSubmit}>
                  <div className="form-row two-columns">
                    <label className="form-field">
                      <span>Nombre y apellidos *</span>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        placeholder="Tu nombre"
                      />
                    </label>
                    <label className="form-field">
                      <span>Empresa *</span>
                      <input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        required
                        placeholder="Nombre de la empresa"
                      />
                    </label>
                  </div>

                  <div className="form-row two-columns">
                    <label className="form-field">
                      <span>Email *</span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="tu@email.com"
                      />
                    </label>
                    <label className="form-field">
                      <span>Teléfono</span>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        placeholder="Opcional"
                      />
                    </label>
                  </div>

                  <div className="form-row two-columns">
                    <label className="form-field">
                      <span>Territorio / ubicación del centro</span>
                      <input
                        type="text"
                        name="territorio"
                        value={formData.territorio}
                        onChange={handleChange}
                        placeholder="Ej. Bizkaia, Gipuzkoa, Araba..."
                      />
                    </label>
                    <label className="form-field">
                      <span>Sector</span>
                      <input
                        type="text"
                        name="sector"
                        value={formData.sector}
                        onChange={handleChange}
                        placeholder="Ej. industrial, servicios ligados a industria..."
                      />
                    </label>
                  </div>

                  <label className="form-field">
                    <span>Interés principal</span>
                    <select name="interes" value={formData.interes} onChange={handleChange}>
                      <option value="ecommerce-portal-ia">E-commerce + portal de clientes + IA</option>
                      <option value="solo-analitica-ia">Solo analítica con IA</option>
                      <option value="solo-presupuestos-ia">Solo IA para presupuestos</option>
                      <option value="proyecto-medida">Proyecto a medida</option>
                    </select>
                  </label>

                  <label className="form-field">
                    <span>Cuéntanos brevemente tu idea</span>
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      rows="5"
                      placeholder="Qué quieres vender, qué procesos quieres mejorar y qué objetivo persigues con la ayuda..."
                    />
                  </label>

                  <label className="form-consent">
                    <input
                      type="checkbox"
                      name="consentimiento"
                      checked={formData.consentimiento}
                      onChange={handleChange}
                      required
                    />
                    <span>
                      He leído la <Link to="/politica-privacidad">política de privacidad</Link> y acepto el tratamiento
                      de mis datos para recibir respuesta a esta solicitud.
                    </span>
                  </label>

                  {error && <p className="form-error">{error}</p>}

                  <button type="submit" className="aplica-ia-button primary form-submit" disabled={enviando}>
                    {enviando ? 'Enviando...' : 'Quiero valorar mi proyecto'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>

      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default AplicaIA2026Page;
